import { useEffect, useState, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { trackEvent } from "@/lib/analytics";
import {
  Phone, Globe, MapPin, BadgeCheck, Sparkles, Share2, Heart, MessageSquare,
  Clock, CheckCircle2, XCircle, Star, Stethoscope, Shield, Lock, UserCog,
  Trophy, Zap, Moon, ImageIcon, X, ArrowRight, Send, Loader2, Calendar as CalIcon,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import StarRating from "@/components/StarRating";
import ProviderCard from "@/components/ProviderCard";
import ProvidersMap from "@/components/ProvidersMap";
import SEO from "@/components/SEO";
import SaveButton from "@/components/SaveButton";
import PhotoUploader from "@/components/PhotoUploader";
import { ThumbsUp } from "lucide-react";
import { DAY_LABELS } from "@/lib/constants";
import { useAuth } from "@/lib/auth";

const TRUST_ITEMS = [
  { key: "verified", icon: BadgeCheck, label: "Verified Business" },
  { key: "licensed_nurses", icon: Stethoscope, label: "Licensed Medical Staff" },
  { key: "medical_director", icon: UserCog, label: "Medical Director on Staff" },
  { key: "hipaa", icon: Lock, label: "HIPAA Compliant" },
  { key: "background", icon: Shield, label: "Background Checked" },
  { key: "top_rated", icon: Trophy, label: "Top Rated 4.5★+" },
  { key: "same_day", icon: Zap, label: "Same-Day Available" },
  { key: "evening", icon: Moon, label: "Evening & Weekend Hours" },
];

export default function Listing() {
  const { slug } = useParams();
  const { user } = useAuth() || {};
  const reviewsRef = useRef(null);
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qa, setQA] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [inquiryService, setInquiryService] = useState(null); // null | service name string

  useEffect(() => {
    setProvider(null);
    api.get(`/providers/${slug}`).then(({ data }) => {
      setProvider(data);
      trackEvent(data.id, "profile_view");
      api.get(`/providers/${data.id}/reviews`).then((r) => setReviews(r.data));
      api.get(`/providers/${data.id}/qa`).then((r) => setQA(r.data));
      api.get(`/providers?city=${encodeURIComponent(data.city)}&limit=4`).then((r) =>
        setSimilar(r.data.items.filter((x) => x.id !== data.id).slice(0, 3))
      );
    });
    
    // Scroll to reviews section if hash is present
    if (window.location.hash === '#reviews') {
      setTimeout(() => {
        const reviewsElement = document.getElementById('reviews');
        if (reviewsElement) {
          reviewsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [slug]);

  if (!provider) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#064E3B] animate-spin" />
      </div>
    );
  }

  const typeLabel = provider.type === "both" ? "Mobile + Clinic" : provider.type === "mobile" ? "Mobile IV" : "IV Clinic";

  const ratingBreakdown = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
    pct: reviews.length ? (reviews.filter((r) => r.rating === star).length / reviews.length) * 100 : 0,
  }));

  const isOpenNow = (() => {
    const now = new Date();
    const dayKey = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][now.getDay()];
    const today = provider.hours?.[dayKey];
    if (!today || today.closed) return false;
    const cur = now.getHours() * 60 + now.getMinutes();
    const [oh, om] = (today.open || "00:00").split(":").map(Number);
    const [ch, cm] = (today.close || "00:00").split(":").map(Number);
    return cur >= oh * 60 + om && cur <= ch * 60 + cm;
  })();

  return (
    <div className="bg-[#FAFAFA]">
      <SEO
        title={`${provider.name} — ${provider.city}, ${provider.state} | IV Therapy Finder`}
        description={`${provider.tagline || provider.description?.slice(0, 155)} · Read ${provider.review_count} reviews and request an appointment online.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          name: provider.name,
          description: provider.description,
          telephone: provider.phone,
          url: provider.website || undefined,
          image: provider.gallery?.slice(0, 5),
          address: {
            "@type": "PostalAddress",
            streetAddress: provider.address,
            addressLocality: provider.city,
            addressRegion: provider.state,
            postalCode: provider.zip_code,
            addressCountry: "US",
          },
          geo: provider.lat ? { "@type": "GeoCoordinates", latitude: provider.lat, longitude: provider.lng } : undefined,
          aggregateRating: provider.review_count > 0 ? {
            "@type": "AggregateRating",
            ratingValue: provider.avg_rating,
            reviewCount: provider.review_count,
            bestRating: 5,
          } : undefined,
        }}
      />
      {/* HEADER */}
      <section className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge variant="secondary" className="bg-[#064E3B]/8 text-[#064E3B] hover:bg-[#064E3B]/10 border-0 font-medium">
              {typeLabel}
            </Badge>
            {provider.verified && (
              <Badge className="bg-[#10B981] hover:bg-[#10B981] border-0 gap-1">
                <BadgeCheck className="w-3 h-3" /> Verified
              </Badge>
            )}
            {provider.featured && (
              <Badge className="bg-[#B4A081] hover:bg-[#B4A081] border-0 gap-1">
                <Sparkles className="w-3 h-3" /> Featured
              </Badge>
            )}
            <Badge variant="outline" className={`${isOpenNow ? "border-[#10B981] text-[#10B981]" : "border-stone-300 text-stone-500"} font-medium gap-1.5`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isOpenNow ? "bg-[#10B981]" : "bg-stone-400"}`} />
              {isOpenNow ? "Open Now" : "Closed"}
            </Badge>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-900 tracking-tight mb-3" data-testid="listing-name">
            {provider.name}
          </h1>
          {provider.tagline && <p className="text-base sm:text-lg text-stone-600 mb-4">{provider.tagline}</p>}

          <div
            className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-6"
            onClick={() => reviewsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            style={{ cursor: 'pointer' }}
          >
            <div className="hover:opacity-80 transition-opacity">
              <StarRating value={provider.avg_rating} count={provider.review_count} showValue size={18} />
            </div>
            <div className="flex items-center gap-1.5 text-sm text-stone-600">
              <MapPin className="w-4 h-4" />
              <span>{provider.city}, {provider.state}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-stone-600">
              <Phone className="w-4 h-4" />
              <a href={`tel:${provider.phone}`} className="hover:text-[#064E3B]">{provider.phone}</a>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button asChild className="bg-[#064E3B] hover:bg-[#053d2f] rounded-full" data-testid="listing-call-btn">
              <a href={`tel:${provider.phone}`} onClick={() => trackEvent(provider.id, "phone_click")}><Phone className="w-4 h-4 mr-2" /> Call Now</a>
            </Button>
            <Button variant="outline" asChild className="rounded-full" data-testid="listing-directions-btn">
              <a
                href={
                  provider.lat && provider.lng
                    ? `https://www.google.com/maps/dir/?api=1&destination=${provider.lat},${provider.lng}&destination_place_id=${encodeURIComponent(provider.name)}`
                    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${provider.name} ${provider.address || ""} ${provider.city || ""} ${provider.state || ""}`)}`
                }
                target="_blank"
                rel="noreferrer"
                onClick={() => trackEvent(provider.id, "directions_click")}
              >
                <MapPin className="w-4 h-4 mr-2" /> Directions
              </a>
            </Button>
            {provider.website && (
              <Button variant="outline" asChild className="rounded-full">
                <a href={provider.website} target="_blank" rel="noreferrer" onClick={() => trackEvent(provider.id, "website_click")}><Globe className="w-4 h-4 mr-2" /> Website</a>
              </Button>
            )}
            <SaveButton providerId={provider.id} providerSlug={provider.slug} variant="full" />
            <Button
              variant="outline"
              size="icon"
              className="rounded-full"
              onClick={async () => {
                trackEvent(provider.id, "share_click");
                const shareData = {
                  title: provider.name,
                  text: `${provider.name} — IV Therapy in ${provider.city}, ${provider.state}. Find more on IVTherapyFinder.com`,
                  url: window.location.href,
                };
                try {
                  if (navigator.share && navigator.canShare?.(shareData)) {
                    await navigator.share(shareData);
                  } else if (navigator.clipboard) {
                    await navigator.clipboard.writeText(window.location.href);
                    toast.success("Link copied to clipboard");
                  } else {
                    window.prompt("Copy this link:", window.location.href);
                  }
                } catch (err) {
                  if (err?.name !== "AbortError") {
                    toast.error("Could not share. Try copying the URL manually.");
                  }
                }
              }}
              aria-label={`Share ${provider.name}`}
              data-testid="listing-share-btn"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-4 grid-rows-2 gap-2 sm:gap-3 h-[280px] sm:h-[420px] rounded-2xl overflow-hidden">
          <button
            onClick={() => { setGalleryIdx(0); setGalleryOpen(true); }}
            className="col-span-4 sm:col-span-2 row-span-2 relative group overflow-hidden"
            data-testid="gallery-main"
          >
            <img src={provider.gallery?.[0] || provider.hero_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </button>
          {(provider.gallery || []).slice(1, 5).map((g, i) => (
            <button
              key={i}
              onClick={() => { setGalleryIdx(i + 1); setGalleryOpen(true); }}
              className="hidden sm:block relative group overflow-hidden"
            >
              <img src={g} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              {i === 3 && provider.gallery.length > 5 && (
                <div className="absolute inset-0 bg-black/55 flex items-center justify-center text-white font-display font-semibold">
                  <ImageIcon className="w-4 h-4 mr-1.5" /> +{provider.gallery.length - 5} more
                </div>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* MAIN GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
        {/* LEFT — content */}
        <div className="space-y-10 min-w-0">
          {/* About */}
          <div>
            <h2 className="font-display text-2xl font-semibold text-stone-900 mb-4">About this provider</h2>
            <DescriptionWithShowMore description={provider.description} providerName={provider.name} />

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="bg-white rounded-xl border border-stone-200 p-4">
                <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-1">Years in business</div>
                <div className="font-display text-xl font-semibold">{provider.years_in_business}+ years</div>
              </div>
              <div className="bg-white rounded-xl border border-stone-200 p-4">
                <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-1">Medical Director</div>
                <div className="font-display text-base font-semibold">{provider.medical_director || "Available"}</div>
              </div>
            </div>

            {provider.specialties?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-1.5">
                {provider.specialties.map((s) => (
                  <Badge key={s} variant="secondary" className="bg-[#064E3B]/5 text-[#064E3B] hover:bg-[#064E3B]/10 border-0 font-normal">{s}</Badge>
                ))}
              </div>
            )}

            {provider.why_choose_us?.length > 0 && (
              <div className="mt-6 bg-[#F3F4F1] rounded-2xl p-6">
                <h3 className="font-display text-base font-semibold text-stone-900 mb-3">Why choose us</h3>
                <ul className="space-y-2">
                  {provider.why_choose_us.map((w) => (
                    <li key={w} className="flex items-start gap-2.5 text-sm text-stone-700">
                      <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" /> {w}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Services menu */}
          <div>
            <h2 className="font-display text-2xl font-semibold text-stone-900 mb-4">Services & Pricing</h2>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="services-grid">
              {provider.services?.map((s, i) => (
                <div key={i} className="bg-white rounded-2xl border border-stone-200 p-5 hover:border-[#064E3B]/40 hover:shadow-md transition-all">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-display text-base font-semibold text-stone-900">{s.name}</h3>
                    <div className="text-right">
                      <div className="font-display text-base font-semibold text-[#064E3B]">${s.price_min}{s.price_max > s.price_min ? `–$${s.price_max}` : ""}</div>
                      <div className="text-[11px] text-stone-500">{s.duration_min} min</div>
                    </div>
                  </div>
                  <p className="text-sm text-stone-600 leading-relaxed mb-4">{s.description}</p>
                  <Button
                    size="sm"
                    className="w-full text-xs bg-[#064E3B] hover:bg-[#053d2f] text-white"
                    onClick={() => setInquiryService(s.name)}
                    data-testid={`inquire-service-${i}`}
                  >
                    Inquire about this service <ArrowRight className="w-3 h-3 ml-1.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Business details */}
          <div>
            <h2 className="font-display text-2xl font-semibold text-stone-900 mb-4">Location & Hours</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <ProvidersMap
                  markers={[{ id: provider.id, lat: provider.lat, lng: provider.lng, name: provider.name, city: provider.city, state: provider.state }]}
                  center={[provider.lat, provider.lng]}
                  zoom={13}
                  fitBounds={false}
                  height="280px"
                />
                <div className="mt-3 text-sm text-stone-700">{provider.address}</div>
                <div className="text-sm text-stone-500">{provider.city}, {provider.state} {provider.zip_code}</div>
                {provider.service_area_cities?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-1.5">Service area</div>
                    <div className="flex flex-wrap gap-1.5">
                      {provider.service_area_cities.map((c) => (
                        <Badge key={c} variant="outline" className="text-xs font-normal">{c}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-5">
                <h3 className="font-display text-base font-semibold flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-[#064E3B]" /> Operating hours
                </h3>
                <table className="w-full text-sm">
                  <tbody>
                    {DAY_LABELS.map((d) => {
                      const h = provider.hours?.[d.key] || { closed: true };
                      return (
                        <tr key={d.key} className="border-b border-stone-100 last:border-0">
                          <td className="py-2 text-stone-600">{d.label}</td>
                          <td className="py-2 text-right font-medium text-stone-900">
                            {h.closed ? <span className="text-stone-400">Closed</span> : <>{h.open} – {h.close}</>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <DetailRow label="Accepts walk-ins" value={provider.accepts_walkins} />
              <DetailRow label="Accepts insurance" value={provider.accepts_insurance} />
              <DetailRow label="Home & hotel visits" value={provider.home_visits} />
              <DetailRow label="Licensed nurses" value={provider.licensed_nurses} />
              <DetailRow label="Medical director" value={!!provider.medical_director} />
              <DetailRow label="Languages" value={provider.languages?.join(", ")} text />
            </div>
          </div>

          {/* Trust badges */}
          <div>
            <h2 className="font-display text-2xl font-semibold text-stone-900 mb-4">Trust & Credentials</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {TRUST_ITEMS.filter((t) => {
                if (t.key === "verified") return provider.verified;
                if (t.key === "licensed_nurses") return provider.licensed_nurses;
                if (t.key === "medical_director") return !!provider.medical_director;
                if (t.key === "top_rated") return provider.avg_rating >= 4.5;
                if (t.key === "same_day") return provider.type !== "clinic" || provider.accepts_walkins;
                return true;
              }).map((t) => (
                <div key={t.key} className="bg-white border border-stone-200 rounded-xl p-4 text-center">
                  <div className="w-10 h-10 mx-auto rounded-full bg-[#064E3B]/8 flex items-center justify-center mb-2">
                    <t.icon className="w-5 h-5 text-[#064E3B]" strokeWidth={1.8} />
                  </div>
                  <div className="text-xs font-medium text-stone-700 leading-tight">{t.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Reviews */}
          <div id="reviews" ref={reviewsRef}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl font-semibold text-stone-900">Reviews & Ratings</h2>
              <ReviewDialog provider={provider} user={user} onAdded={(r) => setReviews([r, ...reviews])} />
            </div>

            <div className="bg-white rounded-2xl border border-stone-200 p-6 mb-5 grid md:grid-cols-[260px_1fr] gap-6">
              <div className="text-center md:border-r md:border-stone-200 md:pr-6">
                <div className="font-display text-5xl font-semibold text-stone-900">{provider.avg_rating?.toFixed(1) || "—"}</div>
                <StarRating value={provider.avg_rating} size={18} className="justify-center mt-1" />
                <div className="text-xs text-stone-500 mt-1">Based on {provider.review_count} reviews</div>
              </div>

              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                <div>
                  {ratingBreakdown.map((r) => (
                    <div key={r.star} className="flex items-center gap-2 text-xs mb-1.5">
                      <span className="w-3 text-stone-600">{r.star}</span>
                      <Star className="w-3 h-3 fill-[#D4A373] text-[#D4A373]" />
                      <Progress value={r.pct} className="flex-1 h-1.5" />
                      <span className="w-6 text-right text-stone-500">{r.count}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  {Object.entries(provider.rating_breakdown || {}).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-xs">
                      <span className="text-stone-600 capitalize">{k}</span>
                      <div className="flex items-center gap-2">
                        <Progress value={(v / 5) * 100} className="w-20 h-1.5" />
                        <span className="font-semibold text-stone-800 w-6">{v.toFixed(1)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {provider.ai_review_summary && (
              <div className="bg-gradient-to-br from-[#064E3B] to-[#0a6b54] text-white rounded-2xl p-5 mb-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold mb-2">
                  <Sparkles className="w-3.5 h-3.5" /> AI Summary
                </div>
                <p className="text-sm leading-relaxed">{provider.ai_review_summary}</p>
              </div>
            )}

            <div className="space-y-4" data-testid="reviews-list">
              {reviews.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-stone-200">
                  <p className="text-sm text-stone-500">No reviews yet. Be the first to share your experience.</p>
                </div>
              ) : reviews.map((r) => (
                <ReviewCard key={r.id} r={r} />
              ))}
            </div>
          </div>

          {/* FAQs + Customer Q&A — unified 2-tab interface */}
          <QASection
            providerId={provider.id}
            providerName={provider.name}
            qa={qa}
            onQAChange={setQA}
          />

          {/* Similar */}
          {similar.length > 0 && (
            <div>
              <h2 className="font-display text-2xl font-semibold text-stone-900 mb-4">Similar providers nearby</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {similar.map((p) => <ProviderCard key={p.id} p={p} compact />)}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — sticky lead gen */}
        <aside className="lg:sticky lg:top-20 self-start">
          <LeadGenPanel provider={provider} />
        </aside>
      </section>

      {/* Gallery dialog */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-4xl p-0 bg-black border-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Gallery</DialogTitle>
            <DialogDescription>Provider photo gallery</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <img src={provider.gallery?.[galleryIdx]} alt="" className="w-full h-[70vh] object-contain" />
            <button onClick={() => setGalleryOpen(false)} className="absolute top-3 right-3 p-2 rounded-full bg-white/90 hover:bg-white">
              <X className="w-4 h-4" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {provider.gallery?.map((_, i) => (
                <button key={i} onClick={() => setGalleryIdx(i)} className={`w-2 h-2 rounded-full transition ${i === galleryIdx ? "bg-white w-6" : "bg-white/50"}`} />
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick inquiry dialog */}
      <InquiryDialog
        provider={provider}
        user={user}
        initialService={inquiryService}
        open={inquiryService !== null}
        onClose={() => setInquiryService(null)}
        onBookInstead={(svc) => {
          setInquiryService(null);
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("ivtf:inquire-service", { detail: { service: svc } }));
            const panel = document.getElementById("request-appointment");
            if (panel) panel.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 50);
        }}
      />
    </div>
  );
}

function DetailRow({ label, value, text = false }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg px-3 py-2.5 flex items-center justify-between">
      <span className="text-xs text-stone-600">{label}</span>
      {text ? (
        <span className="text-xs font-medium text-stone-900 truncate ml-2">{value}</span>
      ) : value ? (
        <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
      ) : (
        <XCircle className="w-4 h-4 text-stone-300" />
      )}
    </div>
  );
}

function LeadGenPanel({ provider }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", preferred_date: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [highlight, setHighlight] = useState(false);

  useEffect(() => {
    const onInquire = (e) => {
      const svc = e?.detail?.service;
      if (svc) {
        setForm((f) => ({ ...f, service: svc }));
        setDone(false);
        setHighlight(true);
        setTimeout(() => setHighlight(false), 1500);
      }
    };
    window.addEventListener("ivtf:inquire-service", onInquire);
    return () => window.removeEventListener("ivtf:inquire-service", onInquire);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/providers/${provider.id}/leads`, form);
      setDone(true);
      toast.success("Request sent! The provider will contact you shortly.");
    } catch {
      toast.error("Could not send request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl border border-stone-200 p-6 text-center">
        <CheckCircle2 className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
        <h3 className="font-display font-semibold text-stone-900 mb-1">Request sent!</h3>
        <p className="text-sm text-stone-600 mb-4">{provider.name} will reach out within 24 hours.</p>
        <Button variant="outline" onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", service: "", preferred_date: "", message: "" }); }} className="w-full">Send another</Button>
      </div>
    );
  }

  return (
    <div id="request-appointment" className={`bg-white rounded-2xl border p-5 shadow-sm transition-all duration-300 ${highlight ? "border-[#10B981] ring-4 ring-[#10B981]/20" : "border-stone-200"}`} data-testid="lead-gen-panel">
      <div className="text-xs uppercase tracking-wider text-[#10B981] font-semibold mb-1">Request appointment</div>
      <h3 className="font-display text-xl font-semibold text-stone-900 mb-1">Book with {provider.name.split(" ").slice(0, 2).join(" ")}</h3>
      <p className="text-xs text-stone-500 mb-4">Free, no obligation. Direct contact within 24 hours.</p>

      <form onSubmit={submit} className="space-y-2.5">
        <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="lead-name" />
        <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" data-testid="lead-phone" />
        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional)" data-testid="lead-email" />
        <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
          <SelectTrigger data-testid="lead-service"><SelectValue placeholder="Service needed" /></SelectTrigger>
          <SelectContent>
            {provider.services?.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} data-testid="lead-date" />
        <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us more (optional)" data-testid="lead-message" />
        <Button type="submit" disabled={submitting} className="w-full bg-[#064E3B] hover:bg-[#053d2f] rounded-lg" data-testid="lead-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Request Appointment <Send className="w-4 h-4 ml-2" /></>)}
        </Button>
      </form>

      <div className="mt-4 pt-4 border-t border-stone-200 grid grid-cols-2 gap-2">
        <Button variant="outline" size="sm" asChild data-testid="lead-call-direct">
          <a href={`tel:${provider.phone}`}><Phone className="w-3.5 h-3.5 mr-1.5" /> Call</a>
        </Button>
        <Button variant="outline" size="sm">
          <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Text
        </Button>
      </div>
    </div>
  );
}

function ReviewCard({ r }) {
  const { user } = useAuth() || {};
  const [voted, setVoted] = useState((r.helpful_user_ids || []).includes(user?.id));
  const [count, setCount] = useState(r.helpful_count || 0);
  const [voting, setVoting] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const vote = async () => {
    if (!user) { toast.error("Sign in to vote"); return; }
    setVoting(true);
    try {
      const { data } = await api.post(`/reviews/${r.id}/helpful`);
      setVoted(data.voted);
      setCount(data.helpful_count);
    } finally {
      setVoting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid={`review-card-${r.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#064E3B] to-[#10B981] flex items-center justify-center text-white font-display font-semibold text-sm">
          {r.user_name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold text-stone-900 text-sm">{r.user_name}</div>
          <div className="flex items-center gap-2 mt-0.5">
            <StarRating value={r.rating} size={13} />
            <span className="text-xs text-stone-500">{new Date(r.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
      <h4 className="font-display font-semibold text-stone-900 mb-1">{r.title}</h4>
      <p className="text-sm text-stone-700 leading-relaxed">{r.body}</p>

      {r.photos?.length > 0 && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {r.photos.map((url, i) => (
            <button key={i} onClick={() => setLightbox(url)} className="w-20 h-20 rounded-lg overflow-hidden bg-stone-100 hover:opacity-90 transition" data-testid={`review-photo-${r.id}-${i}`}>
              <img src={url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 mt-3 pt-3 border-t border-stone-100">
        <button
          onClick={vote}
          disabled={voting}
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition ${voted ? "bg-[#064E3B] text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
          data-testid={`helpful-btn-${r.id}`}
        >
          <ThumbsUp className={`w-3 h-3 ${voted ? "fill-current" : ""}`} /> Helpful {count > 0 && `· ${count}`}
        </button>
      </div>

      {r.owner_reply && (
        <div className="mt-3 pl-4 border-l-2 border-[#064E3B]/30 bg-[#F3F4F1] rounded-r-lg p-3">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#064E3B] mb-1">Response from owner</div>
          <p className="text-sm text-stone-700">{r.owner_reply}</p>
        </div>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)} className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 cursor-pointer">
          <img src={lightbox} alt="" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}
    </div>
  );
}

function ReviewDialog({ provider, user, onAdded }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(searchParams.get("review") === "open");
  const [form, setForm] = useState({
    rating: 0, title: "", body: "",
    cat_professionalism: 5, cat_effectiveness: 5, cat_value: 5, cat_convenience: 5,
    photos: [],
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Clean the ?review=open param once dialog is opened to keep URL tidy
  useEffect(() => {
    if (open && searchParams.get("review") === "open") {
      const next = new URLSearchParams(searchParams);
      next.delete("review");
      setSearchParams(next, { replace: true });
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in to write a review.");
      return;
    }
    if (form.rating < 1 || form.rating > 5) {
      toast.error("Please tap a star to choose your rating.");
      return;
    }
    if (form.body.length < 50) {
      toast.error("Review must be at least 50 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/providers/${provider.id}/reviews`, form);
      onAdded({ ...data, user_name: user.name });
      toast.success("Review posted! Thank you.");
      setOpen(false);
      setForm({ rating: 0, title: "", body: "", cat_professionalism: 5, cat_effectiveness: 5, cat_value: 5, cat_convenience: 5, photos: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid="write-review-btn">
          <Star className="w-4 h-4 mr-1.5" /> Write a Review
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Review {provider.name}</DialogTitle>
          <DialogDescription>Help others find quality care by sharing your experience.</DialogDescription>
        </DialogHeader>
        {!user ? (
          <div className="py-6 text-center">
            <p className="text-sm text-stone-600 mb-4">Sign in to write a review.</p>
            <Button asChild className="bg-[#064E3B] hover:bg-[#053d2f]"><Link to="/login">Sign in</Link></Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-700 font-semibold mb-2 block">
                Overall rating <span className="text-rose-500">*</span>
              </Label>
              <div className="flex items-center gap-1 sm:gap-2" onMouseLeave={() => setHoverRating(0)}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const active = (hoverRating || form.rating) >= n;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm({ ...form, rating: n })}
                      onMouseEnter={() => setHoverRating(n)}
                      aria-label={`${n} star${n > 1 ? "s" : ""}`}
                      aria-pressed={form.rating === n}
                      className="p-2 -m-1 rounded-lg hover:bg-[#D4A373]/10 active:bg-[#D4A373]/20 transition"
                      data-testid={`rating-${n}`}
                    >
                      <Star size={36} className={active ? "fill-[#D4A373] text-[#D4A373]" : "fill-transparent text-stone-300"} strokeWidth={1.5} />
                    </button>
                  );
                })}
                <span className="ml-2 text-sm font-display font-semibold text-stone-700 min-w-[80px]" data-testid="rating-label">
                  {form.rating > 0
                    ? `${form.rating} of 5`
                    : <span className="text-stone-400 font-normal">Tap a star</span>}
                </span>
              </div>
            </div>
            <div>
              <Label className="text-xs">Title</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Summarize your experience" data-testid="review-title" />
            </div>
            <div>
              <Label className="text-xs">Review (min 50 characters)</Label>
              <Textarea required rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="What did you like? What could be better?" data-testid="review-body" />
              <div className="text-xs text-stone-500 mt-1">{form.body.length}/50</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["cat_professionalism", "Professionalism"],
                ["cat_effectiveness", "Effectiveness"],
                ["cat_value", "Value"],
                ["cat_convenience", "Convenience"],
              ].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs">{l}</Label>
                  <Select value={String(form[k])} onValueChange={(v) => setForm({ ...form, [k]: parseInt(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} stars</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs">Add photos (optional)</Label>
              <PhotoUploader
                photos={form.photos}
                onChange={(p) => setForm({ ...form, photos: p })}
                max={4}
                label="Add"
              />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-[#064E3B] hover:bg-[#053d2f]" data-testid="review-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post review"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}


function InquiryDialog({ provider, user, initialService, open, onClose, onBookInstead }) {
  const [form, setForm] = useState({
    service: "",
    name: "",
    phone: "",
    email: "",
    health_conditions: "",
    time_window: "",
    service_questions: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // When dialog opens, seed form with user info + selected service
  useEffect(() => {
    if (open) {
      setSuccess(false);
      setForm({
        service: initialService || "",
        name: user?.name || "",
        phone: user?.phone || "",
        email: user?.email || "",
        health_conditions: "",
        time_window: "",
        service_questions: "",
        message: "",
      });
    }
  }, [open, initialService, user]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please fill in name, phone, and email.");
      return;
    }
    if (!form.service) {
      toast.error("Please select a service.");
      return;
    }
    if (!form.health_conditions.trim() || !form.time_window.trim() || !form.service_questions.trim()) {
      toast.error("Please answer all three questions so the provider can prepare.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/providers/${provider.id}/leads`, { ...form, kind: "inquiry", preferred_date: "" });
      setSuccess(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="inquiry-dialog">
        {success ? (
          <div data-testid="inquiry-success">
            <DialogHeader>
              <DialogTitle className="font-display">Inquiry sent</DialogTitle>
              <DialogDescription className="sr-only">Inquiry confirmation</DialogDescription>
            </DialogHeader>
            <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-5 mt-3 text-center">
              <CheckCircle2 className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
              <p className="font-display font-semibold text-stone-900 mb-1">Your inquiry has been sent.</p>
              <p className="text-sm text-stone-700">
                <strong>{provider.name}</strong> will contact you within 24 hours.
              </p>
            </div>
            <Button onClick={onClose} className="w-full mt-4 bg-[#064E3B] hover:bg-[#053d2f]" data-testid="inquiry-close-success">
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Inquire about a service</DialogTitle>
              <DialogDescription className="sr-only">Quick inquiry form for {provider.name}</DialogDescription>
            </DialogHeader>

            {/* Neutral confirmation banner — green is reserved for success */}
            <div className="bg-slate-100 border border-slate-200 text-slate-800 rounded-lg px-3 py-2.5 text-sm" data-testid="inquiry-banner">
              Selected <strong>"{form.service || "—"}"</strong> — fill in your details to send the request
            </div>

            <form onSubmit={submit} className="space-y-3 mt-1">
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold">Service</Label>
                <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger className="mt-1" data-testid="inquiry-service"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                  <SelectContent>
                    {provider.services?.map((s) => (
                      <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold">Your name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="inquiry-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold">Phone</Label>
                  <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="inquiry-phone" />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold">Email</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="inquiry-email" />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold">Any health conditions we should know about?</Label>
                <Input
                  required
                  value={form.health_conditions}
                  onChange={(e) => setForm({ ...form, health_conditions: e.target.value })}
                  placeholder="e.g. pregnancy, diabetes, none"
                  className="mt-1"
                  data-testid="inquiry-health"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold">Preferred appointment window</Label>
                <Input
                  required
                  value={form.time_window}
                  onChange={(e) => setForm({ ...form, time_window: e.target.value })}
                  placeholder="e.g. Saturday morning, weekday evenings"
                  className="mt-1"
                  data-testid="inquiry-window"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold">Any questions about this service?</Label>
                <Input
                  required
                  value={form.service_questions}
                  onChange={(e) => setForm({ ...form, service_questions: e.target.value })}
                  placeholder="e.g. how long does it take? side effects?"
                  className="mt-1"
                  data-testid="inquiry-questions"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold">Tell us more (optional)</Label>
                <Textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" data-testid="inquiry-message" />
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onBookInstead?.(form.service)}
                  className="sm:flex-1 rounded-full"
                  data-testid="inquiry-book-appointment"
                >
                  <CalIcon className="w-4 h-4 mr-1.5" /> Book Appointment
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="sm:flex-1 bg-[#064E3B] hover:bg-[#053d2f] rounded-full"
                  data-testid="inquiry-send"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Send className="w-4 h-4 mr-1.5" /> Send Quick Inquiry</>)}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FAQSection({ providerId, providerName }) {
  // Kept for backward compatibility; the new <QASection /> renders FAQs inside a tabbed interface.
  // This component is no longer rendered anywhere — left as a no-op for safety.
  return null;
}


// Sanitize TipTap HTML output so only safe formatting tags survive.
const DESC_ALLOWED_TAGS = ["b", "strong", "i", "em", "u", "h1", "h2", "h3", "ul", "ol", "li", "p", "a", "br", "hr"];
const DESC_ALLOWED_ATTR = ["href", "target", "rel", "style"];

function sanitizeDescription(html) {
  if (!html) return "";
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: DESC_ALLOWED_TAGS,
    ALLOWED_ATTR: DESC_ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel):/i,
  });
}

// Walk the sanitized HTML and produce a preview whose visible text length is at most maxChars,
// preserving formatting tags. Returns { html, isTruncated }.
function truncateHtmlByChars(html, maxChars) {
  if (!html) return { html: "", isTruncated: false };
  const container = document.createElement("div");
  container.innerHTML = sanitizeDescription(html);
  if ((container.textContent || "").length <= maxChars) {
    return { html: container.innerHTML, isTruncated: false };
  }
  let remaining = maxChars;
  const result = document.createElement("div");
  const clone = (node, parent) => {
    if (remaining <= 0) return false;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      if (text.length <= remaining) {
        parent.appendChild(document.createTextNode(text));
        remaining -= text.length;
        return true;
      }
      parent.appendChild(document.createTextNode(text.slice(0, remaining) + "…"));
      remaining = 0;
      return false;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = document.createElement(node.nodeName);
      for (const a of node.attributes || []) el.setAttribute(a.name, a.value);
      parent.appendChild(el);
      for (const child of node.childNodes) {
        if (remaining <= 0) break;
        const cont = clone(child, el);
        if (!cont) break;
      }
      return remaining > 0;
    }
    return true;
  };
  for (const child of container.childNodes) {
    if (remaining <= 0) break;
    clone(child, result);
  }
  return { html: result.innerHTML, isTruncated: true };
}

function useDescriptionPreviewLimit() {
  const [limit, setLimit] = useState(() => {
    if (typeof window === "undefined") return 700;
    const w = window.innerWidth;
    if (w <= 768) return 400;
    if (w <= 1024) return 500;
    return 700;
  });
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setLimit(w <= 768 ? 400 : w <= 1024 ? 500 : 700);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return limit;
}

function DescriptionWithShowMore({ description, providerName }) {
  const previewLimit = useDescriptionPreviewLimit();
  const [open, setOpen] = useState(false);

  if (!description || !description.trim()) return null;

  // If it's HTML (from TipTap), use HTML rendering; otherwise render as plain text.
  const isHTML = /<[a-z][\s\S]*>/i.test(description);

  if (!isHTML) {
    const isTruncated = description.length > previewLimit;
    const preview = isTruncated ? description.slice(0, previewLimit) + "…" : description;
    return (
      <div className="mb-6" data-testid="listing-description">
        <p className="text-stone-700 leading-relaxed">{preview}</p>
        {isTruncated && (
          <>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 rounded-full"
              onClick={() => setOpen(true)}
              data-testid="show-more-btn"
            >
              Show more
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="description-modal">
                <DialogHeader>
                  <DialogTitle className="font-display">{providerName} — About Us</DialogTitle>
                  <DialogDescription className="sr-only">Full provider description</DialogDescription>
                </DialogHeader>
                <p className="text-stone-700 leading-relaxed whitespace-pre-wrap">{description}</p>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    );
  }

  const { html: previewHtml, isTruncated } = truncateHtmlByChars(description, previewLimit);
  const fullHtml = sanitizeDescription(description);

  return (
    <div className="mb-6" data-testid="listing-description">
      <div className="tiptap-body text-stone-700" dangerouslySetInnerHTML={{ __html: previewHtml }} />
      {isTruncated && (
        <>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 rounded-full"
            onClick={() => setOpen(true)}
            data-testid="show-more-btn"
          >
            Show more
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="description-modal">
              <DialogHeader>
                <DialogTitle className="font-display">{providerName} — About Us</DialogTitle>
                <DialogDescription className="sr-only">Full provider description</DialogDescription>
              </DialogHeader>
              <div className="tiptap-body text-stone-700" dangerouslySetInnerHTML={{ __html: fullHtml }} />
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}


// ============== Public 2-tab FAQ + Customer Q&A interface ==============
import { formatDistanceToNow } from "date-fns";

const Q_MAX = 300;

function QASection({ providerId, providerName, qa, onQAChange }) {
  const { user } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [tab, setTab] = useState("faq");
  const [askOpen, setAskOpen] = useState(false);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);

  useEffect(() => {
    if (!providerId) return;
    api.get(`/providers/${providerId}/faqs`)
      .then(({ data }) => setFaqs(data))
      .catch(() => setFaqs([]));
  }, [providerId]);

  // Re-fetch QA with viewer's votes attached (only if user is logged in this re-fetches)
  useEffect(() => {
    if (!providerId || !user) return;
    api.get(`/providers/${providerId}/qa`).then(({ data }) => onQAChange(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, user]);

  // JSON-LD FAQPage for SEO (only over FAQs — Google's spec doesn't include user Q&A)
  useEffect(() => {
    if (!faqs.length) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    };
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.dataset.ivtfFaq = providerId;
    tag.textContent = JSON.stringify(schema);
    document.head.appendChild(tag);
    return () => { tag.remove(); };
  }, [faqs, providerId]);

  const handleAskClick = () => {
    if (!user) { setLoginPromptOpen(true); return; }
    setAskOpen(true);
  };

  const handleQuestionSubmitted = (newQ) => {
    onQAChange([newQ, ...qa]);
    setAskOpen(false);
    toast.success(`Your question has been submitted. ${providerName} will respond soon.`);
  };

  const handleVote = async (qid) => {
    if (!user) { setLoginPromptOpen(true); return; }
    try {
      const { data } = await api.post(`/qa/${qid}/vote`, { vote: "helpful" });
      onQAChange(qa.map((q) => q.id === qid ? { ...q, helpful_votes: data.helpful_votes, my_vote: data.vote } : q));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not vote");
    }
  };

  return (
    <div data-testid="public-qa-section">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start mb-4 bg-stone-100">
          <TabsTrigger value="faq" data-testid="qa-tab-faq" className="data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
            Frequently Asked Questions {faqs.length > 0 && <span className="ml-1.5 text-xs opacity-80">({faqs.length})</span>}
          </TabsTrigger>
          <TabsTrigger value="customer" data-testid="qa-tab-customer" className="data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
            Customer Q&A {qa.length > 0 && <span className="ml-1.5 text-xs opacity-80">({qa.length})</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="faq">
          {faqs.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-2xl border border-stone-200">
              <p className="text-sm text-stone-500">No FAQs yet. Contact us for questions about our services.</p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="bg-white rounded-2xl border border-stone-200">
              {faqs.map((f, i) => (
                <AccordionItem key={f.id} value={`faq-${i}`} className="px-5" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-medium hover:no-underline">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-sm text-stone-700 whitespace-pre-wrap">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </TabsContent>

        <TabsContent value="customer">
          {qa.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-2xl border border-stone-200" data-testid="customer-qa-empty">
              <p className="text-sm text-stone-500">No customer questions yet. Be the first to ask!</p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="bg-white rounded-2xl border border-stone-200">
              {qa.map((q, i) => {
                const isAnswered = !!q.answer;
                const helpful = q.helpful_votes || 0;
                return (
                  <AccordionItem key={q.id} value={`cq-${i}`} className="px-5" data-testid={`cq-item-${q.id}`}>
                    <AccordionTrigger className="text-left font-medium hover:no-underline">
                      <div className="flex-1 pr-3">
                        <div className="text-stone-900">{q.question}</div>
                        <div className="flex items-center gap-2 text-xs text-stone-500 font-normal mt-1">
                          <span>{q.user_name}</span>
                          <span>·</span>
                          <span>{formatDistanceToNow(new Date(q.created_at), { addSuffix: true })}</span>
                          {!isAnswered && (
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-0 text-[10px]">⏳ Awaiting response</Badge>
                          )}
                          {helpful > 0 && (
                            <span className="text-stone-400">· {helpful} found helpful</span>
                          )}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      {isAnswered ? (
                        <div>
                          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#064E3B] mb-1">Response from {providerName}</div>
                          <p className="text-sm text-stone-700 whitespace-pre-wrap">{q.answer}</p>
                          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-stone-100">
                            <Button
                              size="sm"
                              variant="outline"
                              className={`h-8 ${q.my_vote === "helpful" ? "bg-[#10B981]/10 border-[#10B981] text-[#10B981]" : ""}`}
                              onClick={() => handleVote(q.id)}
                              data-testid={`cq-helpful-${q.id}`}
                            >
                              👍 Helpful{helpful > 0 && ` (${helpful})`}
                            </Button>
                            {helpful > 0 && (
                              <span className="text-xs text-stone-500">{helpful} {helpful === 1 ? "person" : "people"} found this helpful</span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-stone-500 italic">Awaiting response from {providerName}.</p>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}

          <div className="mt-5 flex justify-center">
            <Button className="bg-[#064E3B] hover:bg-[#053d2f] rounded-full px-6" onClick={handleAskClick} data-testid="ask-question-btn">
              <MessageSquare className="w-4 h-4 mr-2" /> Ask a Question
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <AskQuestionDialog
        open={askOpen}
        onClose={() => setAskOpen(false)}
        user={user}
        providerId={providerId}
        onSubmitted={handleQuestionSubmitted}
      />

      <Dialog open={loginPromptOpen} onOpenChange={setLoginPromptOpen}>
        <DialogContent className="max-w-sm" data-testid="login-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Please log in to ask a question</DialogTitle>
            <DialogDescription className="text-sm">
              Sign in or create a free account to ask {providerName} a question.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" asChild className="sm:flex-1"><Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`}>Log In</Link></Button>
            <Button asChild className="sm:flex-1 bg-[#064E3B] hover:bg-[#053d2f]"><Link to={`/signup?next=${encodeURIComponent(window.location.pathname)}`}>Sign Up</Link></Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AskQuestionDialog({ open, onClose, user, providerId, onSubmitted }) {
  const [question, setQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) setQuestion(""); }, [open]);

  const onChange = (e) => {
    const v = e.target.value;
    if (v.length <= Q_MAX) setQuestion(v);
  };

  const submit = async () => {
    if (!question.trim()) { toast.error("Please type a question first."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/providers/${providerId}/qa`, { question, user_name: user?.name || "Customer" });
      onSubmitted(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit question");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;
  const len = question.length;
  const limitClass = len >= Q_MAX ? "text-rose-600 font-semibold" : len > Q_MAX * 0.8 ? "text-amber-600" : "text-stone-500";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="ask-question-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Ask a question</DialogTitle>
          <DialogDescription className="sr-only">Submit a public question to this provider</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Your name</Label>
              <Input value={user.name} readOnly className="bg-stone-50 cursor-not-allowed" data-testid="ask-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Your email</Label>
              <Input value={user.email} readOnly className="bg-stone-50 cursor-not-allowed" data-testid="ask-email" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Your question <span className="text-rose-500">*</span></Label>
            <Textarea
              value={question}
              onChange={onChange}
              rows={4}
              placeholder="e.g. Do you offer same-day appointments? What insurance do you accept?"
              maxLength={Q_MAX}
              autoFocus
              data-testid="ask-question-text"
            />
            <div className={`text-[11px] mt-1 flex justify-between ${limitClass}`} data-testid="ask-counter">
              <span>{len >= Q_MAX ? `You have reached the maximum character limit (${Q_MAX})` : ""}</span>
              <span>{len}/{Q_MAX} characters</span>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 mt-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button className="bg-[#064E3B] hover:bg-[#053d2f]" onClick={submit} disabled={submitting || !question.trim()} data-testid="ask-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ask Question"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

